import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clamp } from 'lodash';

import { type ValueOf } from '~/common/types';

import useEvent from '@/shared/hooks/useEvent';
import useSet from '@/shared/hooks/useSet';

type AllowedStepperDirectionKey = ValueOf<typeof ALLOWED_STEPPER_DIRECTION_KEYS>;

export type IndexChangeData<T> = {
  index: number;
  item: T | null;
  offset?: number;
};

export type IndexValue<T> = number | ((item: T) => boolean) | null | undefined;

type OverflowMode = ValueOf<typeof OVERFLOW_MODES>;

export type StepperDirection = ValueOf<typeof STEPPER_DIRECTIONS>;

export type StepperUtils = ReturnType<typeof useListStepper>[1];

type StepperOptions<T> = {
  allowedDirection?: AllowedStepperDirectionKey;
  defaultIndex?: IndexValue<T>;
  index?: IndexValue<T>;
  onIndexChange?: (data: IndexChangeData<T>) => void;
  overflowDirection?: AllowedStepperDirectionKey;
  overflowMode?: OverflowMode;
};

export type StepperState<T> = ReturnType<typeof useListStepper<T>>[0];

const OVERFLOW_MODES = {
  CLAMP: 'clamp',
  FREE: 'free',
  LOOP: 'loop',
} as const;

const STEPPER_DIRECTIONS = {
  NEXT: 'next',
  PREV: 'prev',
} as const;

const ALLOWED_STEPPER_DIRECTION_KEYS = {
  BOTH: 'both',
  NEXT: STEPPER_DIRECTIONS.NEXT,
  NONE: 'none',
  PREV: STEPPER_DIRECTIONS.PREV,
} as const;

const ALLOWED_STEPPER_DIRECTIONS = {
  [ALLOWED_STEPPER_DIRECTION_KEYS.BOTH]: [STEPPER_DIRECTIONS.NEXT, STEPPER_DIRECTIONS.PREV],
  [ALLOWED_STEPPER_DIRECTION_KEYS.NEXT]: [STEPPER_DIRECTIONS.NEXT],
  [ALLOWED_STEPPER_DIRECTION_KEYS.NONE]: [],
  [ALLOWED_STEPPER_DIRECTION_KEYS.PREV]: [STEPPER_DIRECTIONS.PREV],
} as const;

const STEPPER_OFFSETS = {
  [STEPPER_DIRECTIONS.NEXT]: 1,
  [STEPPER_DIRECTIONS.PREV]: -1,
} as const;

const useListStepper = <T>(items: readonly T[] | undefined, options?: StepperOptions<T>) => {
  const offsetRef = useRef<number>();

  const allowedStepperDirectionsKey =
    options?.allowedDirection ?? ALLOWED_STEPPER_DIRECTION_KEYS.BOTH;
  const overflowMode = options?.overflowMode ?? OVERFLOW_MODES.CLAMP;

  const allowedStepperDirectionsSet = useSet(
    ALLOWED_STEPPER_DIRECTIONS[allowedStepperDirectionsKey],
  );

  const itemsRange = {
    max: items ? Math.max(0, items.length - 1) : 0,
    min: 0,
    size: items?.length ?? 0,
  };

  const applyBounds = useCallback(
    (value: number) => {
      if (overflowMode === OVERFLOW_MODES.FREE) return value;

      const clampedValue = clamp(value, itemsRange.min, itemsRange.max);

      if (overflowMode === OVERFLOW_MODES.CLAMP) return clampedValue;
      if (value === clampedValue) return value;

      return itemsRange.size ? ((value % itemsRange.size) + itemsRange.size) % itemsRange.size : 0;
    },
    [itemsRange.max, itemsRange.min, itemsRange.size, overflowMode],
  );

  const sanitizeIndex = useCallback(
    (index: IndexValue<T>) => {
      if (!items || index == null) return itemsRange.min;
      if (typeof index === 'number') return applyBounds(index);

      const itemIndex = items.findIndex(index);

      return itemIndex === -1 ? itemsRange.min : applyBounds(itemIndex);
    },
    [applyBounds, items, itemsRange.min],
  );

  const [indexInternal, setIndexInternal] = useState(() => sanitizeIndex(options?.defaultIndex));

  const isControlledByParent = options?.index != null;

  const currentIndex = isControlledByParent ? sanitizeIndex(options.index) : indexInternal;
  const currentItem = items?.[currentIndex] ?? null;
  const firstItem = items?.[itemsRange.min] ?? null;
  const lastItem = items?.[itemsRange.max] ?? null;

  const onIndexChangeInternal = useEvent(options?.onIndexChange);

  const handleIndexChange = useCallback(
    (index: number, offset?: number) => {
      if (!items) return;

      const item = items[index] ?? null;

      onIndexChangeInternal({
        index,
        item,
        offset,
      });
    },
    [items, onIndexChangeInternal],
  );

  const stepByOffset = useCallback(
    (offset: number) => {
      if (!items) return;

      if (isControlledByParent) {
        const nextIndex = applyBounds(currentIndex + offset);

        handleIndexChange(nextIndex, offset);

        return;
      }

      offsetRef.current = offset;

      setIndexInternal((prevIndex) => applyBounds(prevIndex + offset));
    },
    [applyBounds, currentIndex, handleIndexChange, isControlledByParent, items],
  );

  const allowOverflow = overflowMode !== OVERFLOW_MODES.CLAMP;
  const enabled = !!items && !allowedStepperDirectionsSet.isEmpty;
  const nextEnabled =
    enabled &&
    allowedStepperDirectionsSet.has(STEPPER_DIRECTIONS.NEXT) &&
    (allowOverflow || currentIndex < itemsRange.max);
  const prevEnabled =
    enabled &&
    allowedStepperDirectionsSet.has(STEPPER_DIRECTIONS.PREV) &&
    (allowOverflow || currentIndex > itemsRange.min);

  const toNextItem = useCallback(() => {
    if (nextEnabled) stepByOffset(STEPPER_OFFSETS[STEPPER_DIRECTIONS.NEXT]);
  }, [nextEnabled, stepByOffset]);

  const toPrevItem = useCallback(() => {
    if (prevEnabled) stepByOffset(STEPPER_OFFSETS[STEPPER_DIRECTIONS.PREV]);
  }, [prevEnabled, stepByOffset]);

  const state = useMemo(
    () => ({
      currentIndex,
      currentItem,
      firstItem,
      lastItem,
      nextEnabled,
      prevEnabled,
    }),
    [currentIndex, currentItem, firstItem, lastItem, nextEnabled, prevEnabled],
  );

  const utils = useMemo(
    () => ({
      [STEPPER_DIRECTIONS.NEXT]: toNextItem,
      [STEPPER_DIRECTIONS.PREV]: toPrevItem,
    }),
    [toNextItem, toPrevItem],
  );

  useEffect(() => {
    if (!isControlledByParent) handleIndexChange(currentIndex, offsetRef.current);
  }, [currentIndex, handleIndexChange, isControlledByParent]);

  return [state, utils] as const;
};

export default useListStepper;
